/**
 * Org Admin Portal — accessible to org_admin users.
 * Houses Policy Templates and AI Skills management.
 * Route: /org-admin (registered in features/revisr/index.ts)
 */
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useUserPermissions } from '@foundation/hooks/useUserPermissions';
import { useActiveWorkspaceRole } from '../../header/useActiveWorkspaceRole';
import { OrgAdminSidebar, OrgAdminTab } from '../components/admin/OrgAdminSidebar';
import { TemplatesSection } from '@foundation/components/settings/TemplatesSection';
import { AiSkillsSection } from '@foundation/components/settings/AiSkillsSection';

export const OrgAdminPortal: React.FC = () => {
  // isOrgAdmin is scoped to the active workspace
  const { isOrgAdmin, isLoading: roleLoading } = useActiveWorkspaceRole();
  // isPlatformAdmin comes from the platform-level permissions hook
  const { permissions: userPermissions, loading: permLoading } = useUserPermissions();
  const isPlatformAdmin = userPermissions?.isPlatformAdmin ?? false;
  const isLoading = roleLoading || permLoading;
  const [activeTab, setActiveTab] = useState<OrgAdminTab>('policy-templates');

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Only org_admin (and platform admins who also manage orgs) can access
  if (!isOrgAdmin && !isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="flex h-screen">
        <OrgAdminSidebar activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {activeTab === 'policy-templates' ? (
              <TemplatesSection />
            ) : (
              <AiSkillsSection />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
